import {
  ElNotification,
  type NotificationHandle,
  type NotificationOptions,
} from "element-plus";
import { useTranslation } from "i18next-vue";
import { ref } from "vue";

/**
 * Outil dessin ouvert (cf. CanvaLayout) : aucun toast. Ils masquaient le plan et
 * s'empilaient à chaque bascule d'option. Seuls passent ceux marqués
 * `allowOnCanvas` (téléchargement / export).
 */
export const areToastsMutedOnCanvas = ref(false);

export interface ToastOptions {
  /** Afficher même dans l'outil dessin. Réservé au téléchargement / export. */
  allowOnCanvas?: boolean;
}

/** Retourné à la place d'une notification muette, pour que `close()` reste sûr. */
const MUTED_HANDLE: NotificationHandle = { close: () => {} };

export function useToast() {
  const { t } = useTranslation();
  const defaultParams: Partial<NotificationOptions> = {
    position: "bottom-right",
  };

  const notify = (
    params: Partial<NotificationOptions>,
    options?: ToastOptions
  ): NotificationHandle => {
    if (areToastsMutedOnCanvas.value && !options?.allowOnCanvas) {
      return MUTED_HANDLE;
    }
    return ElNotification({ ...defaultParams, ...params });
  };

  const showSuccess = (
    message: string,
    params?: Record<string, unknown>,
    options?: ToastOptions
  ) => {
    notify({ title: t(message, params ?? {}), type: "success" }, options);
  };

  const showDanger = (
    message: string,
    params?: Record<string, unknown>,
    options?: ToastOptions
  ) => {
    notify({ title: t(message, params ?? {}), type: "error" }, options);
  };

  const showWarning = (
    message: string,
    params?: Record<string, unknown>,
    options?: ToastOptions
  ) => {
    notify({ title: t(message, params ?? {}), type: "warning" }, options);
  };

  const showSecondary = (
    message: string,
    params?: Record<string, unknown>,
    options?: ToastOptions
  ) => {
    notify({ title: t(message, params ?? {}), type: "info" }, options);
  };

  const showLoading = (
    message: string,
    params?: Record<string, unknown>,
    options?: ToastOptions
  ) =>
    notify(
      {
        title: t(message, params ?? {}),
        type: "info",
        icon: "el-icon-loading",
        duration: 0,
      },
      options
    );

  const showToast = (
    path: string,
    type: "success" | "error" | "warning" | "info" = "success"
  ) => {
    notify({ title: t(`toast:${path}`), type });
  };

  return {
    showSuccess,
    showDanger,
    showWarning,
    showSecondary,
    showLoading,
    showToast,
  };
}
